import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { localMidnight } from "./TaskDetailDrawer";
import { PRIORITY_META, STATUS_META, BOARD_COLUMNS, type TaskStatus } from "./taskMeta";

interface Props {
  open: boolean;
  projectId: string | null;
  parentId: string | null;
  onClose: () => void;
  onCreated: () => void;
}

export function TaskFormDrawer({ open, projectId, parentId, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [status, setStatus] = useState<TaskStatus>("backlog");
  const [priority, setPriority] = useState("medium");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [estimate, setEstimate] = useState("");
  const [milestone, setMilestone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parentQ = trpc.project.task.get.useQuery({ id: parentId ?? "" }, { enabled: open && !!parentId, staleTime: 10_000 });
  const createMut = trpc.project.task.create.useMutation();

  useEffect(() => {
    if (!open) return;
    setTitle(""); setDesc(""); setStatus("backlog"); setPriority("medium");
    setStart(""); setEnd(""); setEstimate(""); setMilestone(false); setErr(null);
  }, [open, parentId]);

  async function submit() {
    setErr(null);
    if (!projectId) { setErr("Önce bir proje seçin."); return; }
    if (!title.trim()) { setErr("Görev başlığı zorunlu."); return; }
    if (start && end && end < start) { setErr("Bitiş tarihi başlangıçtan önce olamaz."); return; }
    const est = estimate.trim() ? Number(estimate.replace(",", ".")) : null;
    if (est != null && (!Number.isFinite(est) || est <= 0)) { setErr("Tahmini süre pozitif bir sayı olmalı."); return; }
    try {
      await createMut.mutateAsync({
        projectId, parentId: parentId ?? null,
        title: title.trim(), description: desc.trim() || null,
        status, priority: priority as "low" | "medium" | "high" | "critical",
        plannedStart: start ? localMidnight(start) : null,
        plannedEnd: end ? localMidnight(end) : null,
        estimateHours: est,
        isMilestone: milestone,
      });
      await qc.invalidateQueries();
      onCreated();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <Drawer
      open={open} onClose={onClose} size="md" dismissible={false}
      title={parentId ? "Yeni Alt Görev" : "Yeni Görev"}
      subtitle={parentId && parentQ.data ? `Üst görev: ${parentQ.data.code} — ${parentQ.data.title}` : "Kod otomatik üretilir (GRV-yıl-sıra)"}
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          <button type="button" className="btn-md primary" onClick={submit} disabled={createMut.isPending}>
            {createMut.isPending ? "Oluşturuluyor…" : "Oluştur"}
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
        </div>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="form-field">
          <label className="lbl" htmlFor="tf-title">Başlık <span className="req">*</span></label>
          <input id="tf-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Örn. Elektrik panosu montajı" autoFocus />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="tf-desc">Görev Tanımı</label>
          <textarea id="tf-desc" className="form-textarea" rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="tf-status">Durum</label>
            <select id="tf-status" className="form-select" value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
              {BOARD_COLUMNS.map((s) => <option key={s} value={s}>{STATUS_META.task[s].label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="tf-pri">Öncelik</label>
            <select id="tf-pri" className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="tf-start">Planlanan Başlangıç</label>
            <input id="tf-start" className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="tf-end">Planlanan Bitiş</label>
            <input id="tf-end" className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="tf-est">Tahmini Süre (saat)</label>
          <input id="tf-est" className="form-input" value={estimate} onChange={(e) => setEstimate(e.target.value)} inputMode="decimal" placeholder="Örn. 16" />
          <span className="help">Üst görevin ilerlemesi alt görevlerin tahmini sürelerine göre ağırlıklandırılır.</span>
        </div>
        <label className="form-checkbox">
          <input type="checkbox" checked={milestone} onChange={(e) => setMilestone(e.target.checked)} />
          <span>Kilometre taşı</span>
        </label>
      </div>
    </Drawer>
  );
}
